// src/data/crisisResources.js
//
// Canada-correct support lines — the FLOOR beneath an organization's own
// support contact / EAP. Shown in the Talk-to-Someone modal (always, so a
// member of an org that has configured nothing still has somewhere to go),
// on worksheet review steps, and inline in the AI Coach when a member's own
// words match a theme.
//
// Each line: { id, name, parts: [{ text, href? }], note, audiences?, region?, urgent?, themes? }
//   parts    — rendered inline; parts with href are tappable (tel:/sms:/https:)
//   audiences— member types the line is for (omit = everyone)
//   region   — label shown when a line is provincial (clients today are Alberta-based)
//   themes   — ids from THEMES. linesForThemes() surfaces the line when what a
//              member typed matches (utils/crisisDetect.js). The line list and
//              its themes follow Hockey Alberta's Safety & Support Resource List
//              (2026-09-26), which asked for resources to be offered in context
//              rather than as one long list.

// What a theme is called when we tell a member why a line was offered.
export const THEMES = {
    danger: 'someone being in danger right now',
    crisis: 'thoughts of suicide or self-harm',
    bullying: 'bullying or being targeted',
    sexual_violence: 'unwanted sexual contact or pressure',
    family_violence: 'not feeling safe at home',
    substance: 'alcohol, drugs or vaping',
    indigenous: 'culturally grounded support',
    find_help: 'not knowing where to start'
};

const EMERGENCY = {
    id: '911', name: '911 — Emergency', urgent: true,
    parts: [{ text: 'Call 911', href: 'tel:911' }],
    note: 'If you or someone else is in immediate danger or seriously hurt',
    themes: ['danger']
};

const LINE_988 = {
    id: '988', name: '9-8-8 Suicide Crisis Helpline', urgent: true,
    parts: [{ text: 'Call 9-8-8', href: 'tel:988' }, { text: ' or ' }, { text: 'text 9-8-8', href: 'sms:988' }],
    note: 'Free, 24/7, anywhere in Canada — if things feel like too much right now, or you are worried about someone else',
    themes: ['crisis']
};

const KIDS_HELP_PHONE = {
    id: 'kht', name: 'Kids Help Phone', audiences: ['child', 'teen'],
    parts: [
        { text: 'Text CONNECT to 686868', href: 'sms:686868?body=CONNECT' }, { text: ' · ' },
        { text: '1-800-668-6868', href: 'tel:1-800-668-6868' }, { text: ' · ' },
        { text: 'chat online', href: 'https://kidshelpphone.ca' }
    ],
    note: 'Free, 24/7, confidential — for young people, even when you are not sure what kind of help you need. Many languages. Indigenous youth can text FIRST NATIONS, INUIT or METIS to 686868.',
    themes: ['bullying', 'find_help']
};

const HOPE_FOR_WELLNESS = {
    id: 'hope', name: 'Hope for Wellness Help Line',
    parts: [{ text: '1-855-242-3310', href: 'tel:1-855-242-3310' }, { text: ' · ' }, { text: 'hopeforwellness.ca', href: 'https://www.hopeforwellness.ca' }],
    note: 'For First Nations, Inuit and Métis — 24/7 counselling by phone or chat, in English and French; Cree, Ojibway and Inuktitut on request',
    themes: ['indigenous']
};

// Alberta Health Services routes addiction and mental-health calls through
// Health Link — one number, follow the prompts.
const HEALTH_LINK_811 = {
    id: 'ab-811', name: 'Health Link 811', region: 'Alberta',
    parts: [{ text: 'Call 811', href: 'tel:811' }],
    note: '24/7 — health advice, and the way in to addiction and mental health services in Alberta. Follow the prompts for Addiction and Mental Health.',
    themes: ['substance', 'find_help']
};

const KICKSTAND = {
    id: 'ab-kickstand', name: 'Kickstand', region: 'Alberta', audiences: ['child', 'teen'],
    parts: [{ text: 'mykickstand.ca', href: 'https://mykickstand.ca' }],
    note: 'Free, confidential counselling, substance-use support and peer support for ages 11–25. Same-day virtual appointments; no referral, health card or payment needed.',
    themes: ['substance']
};

const AB_BULLYING = {
    id: 'ab-bullying', name: 'Alberta Bullying Helpline', region: 'Alberta',
    parts: [{ text: 'Call or text 310-1818', href: 'tel:310-1818' }, { text: ' · ' }, { text: 'chat online', href: 'https://www.alberta.ca/bullying-get-help' }],
    note: '24/7 support, information and referrals for anyone experiencing bullying — including in the dressing room or a group chat',
    themes: ['bullying']
};

const AB_ONE_LINE = {
    id: 'ab-one-line', name: 'Alberta One-Line for Sexual Violence', region: 'Alberta',
    parts: [{ text: 'Call or text 1-866-403-8000', href: 'tel:1-866-403-8000' }],
    note: 'Support, information and referrals for anyone affected by sexual violence — 9 am to 9 pm daily, 170+ languages',
    themes: ['sexual_violence']
};

const AB_FAMILY_VIOLENCE = {
    id: 'ab-family-violence', name: 'Family Violence Info Line', region: 'Alberta',
    parts: [{ text: 'Call or text 310-1818', href: 'tel:310-1818' }],
    note: '24/7, confidential — family, domestic or dating violence, or not feeling safe at home. Phone support in 170+ languages',
    themes: ['family_violence']
};

const AB_211 = {
    id: 'ab-211', name: '211 Alberta', region: 'Alberta',
    parts: [{ text: 'Call 211', href: 'tel:211' }, { text: ' or ' }, { text: 'text INFO to 211', href: 'sms:211?body=INFO' }],
    note: 'When you need support but do not know which service is right — counselling, community and family resources, 24/7',
    themes: ['find_help']
};

const CMHA = {
    id: 'cmha', name: 'Canadian Mental Health Association',
    parts: [{ text: 'cmha.ca/find-help', href: 'https://cmha.ca/find-help/' }],
    note: 'Find local programs and support across Canada'
};

// The national floor: every member sees these (youth-only lines gated by age).
export const CANADA_CRISIS = [LINE_988, KIDS_HELP_PHONE, HOPE_FOR_WELLNESS];

// Provincial lines a member can explore, and that themes can surface.
export const ALBERTA_LINES = [HEALTH_LINK_811, KICKSTAND, AB_BULLYING, AB_ONE_LINE, AB_FAMILY_VIOLENCE, AB_211];

export const TOPIC_RESOURCES = {
    'anxiety': [KICKSTAND, HEALTH_LINK_811, CMHA],
    'depression': [KICKSTAND, HEALTH_LINK_811, CMHA],
    'ptsd-trauma': [HEALTH_LINK_811, CMHA],
    'adhd': [
        { id: 'caddac', name: 'CADDAC', parts: [{ text: 'caddac.ca', href: 'https://caddac.ca' }], note: 'Centre for ADHD Awareness, Canada — resources and directory' },
        CMHA
    ],
    'addictions': [HEALTH_LINK_811, KICKSTAND, CMHA],
    'gambling': [
        HEALTH_LINK_811,
        { id: 'ga', name: 'Gamblers Anonymous', parts: [{ text: 'gamblersanonymous.org', href: 'https://www.gamblersanonymous.org/' }], note: 'Meetings across Canada' }
    ],
    'eating-disorders': [
        { id: 'nedic', name: 'NEDIC helpline', parts: [{ text: '1-866-633-4220', href: 'tel:1-866-633-4220' }, { text: ' · ' }, { text: 'nedic.ca', href: 'https://nedic.ca' }], note: 'National Eating Disorder Information Centre — toll-free, Canada' }
    ],
    'sleep': [HEALTH_LINK_811],
    'resilience': [HEALTH_LINK_811],
    'pain-injury': [HEALTH_LINK_811],
    // Hockey Alberta's Respect & Belonging page: bullying and belonging are the themes.
    'custom-respect-and-belonging': [AB_BULLYING, KICKSTAND]
};

// Is this member a minor, for the purpose of which lines to show?
//
// AGE decides, not the persona they picked at join. The original check read
// `memberType === 'child' || memberType === 'teen'` — but 'teen' is an AGE TIER,
// never a member type (the ids are athlete / spouse / child), so that half never
// fired, and a minor who joined as an athlete was treated as an adult. For a
// youth-sport org every player is exactly that: a 13-year-old "Player" was shown
// the adult lines and not Kids Help Phone. `memberType === 'child'` is kept as a
// fallback for legacy docs that carry a persona but no age signal.
export const isYouthMember = ({ memberType, ageTier } = {}) =>
    ageTier === 'kid' || ageTier === 'teen' || memberType === 'child';

const applies = (line, isYouth) => !line.audiences || isYouth;
const dedupe = (lines) => {
    const seen = new Set();
    return lines.filter(l => { if (seen.has(l.id)) return false; seen.add(l.id); return true; });
};

// Lines for this member: topic-specific + national. Youth-only lines are shown
// only to youth — and lead the list for them, because a line built for young
// people is the right first door for a young person.
export const resourcesFor = (topicId, memberType, ageTier) => {
    const isYouth = isYouthMember({ memberType, ageTier });
    const youthLines = isYouth ? CANADA_CRISIS.filter(l => l.audiences) : [];
    const national = CANADA_CRISIS.filter(l => !l.audiences);
    const topic = ((topicId && TOPIC_RESOURCES[topicId]) || []).filter(l => applies(l, isYouth));
    return dedupe([...youthLines, ...topic, ...national]);
};

// Every line this member could use — the broader list a member can choose to
// explore, behind the contextual few.
export const allResourcesFor = (memberType, ageTier) => {
    const isYouth = isYouthMember({ memberType, ageTier });
    return dedupe([...CANADA_CRISIS, ...ALBERTA_LINES].filter(l => applies(l, isYouth)));
};

// Lines for what a member just said (theme ids from detectSupportThemes).
// Youth lines lead because the pool is ordered that way.
export const linesForThemes = (themes, memberType, ageTier) => {
    if (!Array.isArray(themes) || themes.length === 0) return [];
    const isYouth = isYouthMember({ memberType, ageTier });
    const pool = [EMERGENCY, ...CANADA_CRISIS, ...ALBERTA_LINES];
    return dedupe(pool.filter(l => applies(l, isYouth) && (l.themes || []).some(t => themes.includes(t))));
};

export const themeLabels = (themes = []) => themes.map(t => THEMES[t]).filter(Boolean);
